/**
 * Periodic-table data — 118 elements with everything the AtomSource
 * and AtomPanel need: symbol, full name, atomic number, group/period
 * coordinates for grid placement, electron shell occupancy, and a
 * category tag for color-coding.
 *
 * Data is canonical IUPAC + Bohr-model shell counts (the "K, L, M, N…
 * shell holds 2, 8, 18, 32… electrons" convention). Lanthanides La-Lu
 * and actinides Ac-Lr live in the floating rows below the main grid;
 * we represent that via period 8 / 9 group columns 4..17 so the UI
 * grid can place them without special-casing.
 */

export type ElementCategory =
  | 'alkali-metal'
  | 'alkaline-earth'
  | 'transition-metal'
  | 'post-transition-metal'
  | 'metalloid'
  | 'nonmetal'
  | 'halogen'
  | 'noble-gas'
  | 'lanthanide'
  | 'actinide';

export interface Element {
  /** Atomic number (1..118). */
  readonly atomicNumber: number;
  /** Element symbol — H, He, Li, Au, etc. */
  readonly symbol: string;
  /** Full English name, lowercase capitalization (matches IUPAC). */
  readonly name: string;
  /** Periodic table column 1..18. Lanthanides + actinides use column
   *  values that match their position in the floating rows (4..17). */
  readonly group: number;
  /** Periodic table row 1..7 for the main grid; 8 = lanthanide row,
   *  9 = actinide row in the UI. */
  readonly period: number;
  /** Electron occupancy per shell, K shell first. Length 1..7.
   *  Sum equals the atomic number. */
  readonly shells: readonly number[];
  /** Visual category — drives the cell color in the periodic-table UI. */
  readonly category: ElementCategory;
}

/**
 * The full set, ordered by atomic number. The `group` for lanthanides
 * and actinides is set to where they appear in the floating rows
 * (4..17 for the 14 elements each), period 8 / 9 to render below
 * the main 7-row grid.
 */
export const ELEMENTS: readonly Element[] = [
  // Period 1
  { atomicNumber: 1, symbol: 'H', name: 'Hydrogen', group: 1, period: 1, shells: [1], category: 'nonmetal' },
  { atomicNumber: 2, symbol: 'He', name: 'Helium', group: 18, period: 1, shells: [2], category: 'noble-gas' },
  // Period 2
  { atomicNumber: 3, symbol: 'Li', name: 'Lithium', group: 1, period: 2, shells: [2, 1], category: 'alkali-metal' },
  { atomicNumber: 4, symbol: 'Be', name: 'Beryllium', group: 2, period: 2, shells: [2, 2], category: 'alkaline-earth' },
  { atomicNumber: 5, symbol: 'B', name: 'Boron', group: 13, period: 2, shells: [2, 3], category: 'metalloid' },
  { atomicNumber: 6, symbol: 'C', name: 'Carbon', group: 14, period: 2, shells: [2, 4], category: 'nonmetal' },
  { atomicNumber: 7, symbol: 'N', name: 'Nitrogen', group: 15, period: 2, shells: [2, 5], category: 'nonmetal' },
  { atomicNumber: 8, symbol: 'O', name: 'Oxygen', group: 16, period: 2, shells: [2, 6], category: 'nonmetal' },
  { atomicNumber: 9, symbol: 'F', name: 'Fluorine', group: 17, period: 2, shells: [2, 7], category: 'halogen' },
  { atomicNumber: 10, symbol: 'Ne', name: 'Neon', group: 18, period: 2, shells: [2, 8], category: 'noble-gas' },
  // Period 3
  { atomicNumber: 11, symbol: 'Na', name: 'Sodium', group: 1, period: 3, shells: [2, 8, 1], category: 'alkali-metal' },
  { atomicNumber: 12, symbol: 'Mg', name: 'Magnesium', group: 2, period: 3, shells: [2, 8, 2], category: 'alkaline-earth' },
  { atomicNumber: 13, symbol: 'Al', name: 'Aluminum', group: 13, period: 3, shells: [2, 8, 3], category: 'post-transition-metal' },
  { atomicNumber: 14, symbol: 'Si', name: 'Silicon', group: 14, period: 3, shells: [2, 8, 4], category: 'metalloid' },
  { atomicNumber: 15, symbol: 'P', name: 'Phosphorus', group: 15, period: 3, shells: [2, 8, 5], category: 'nonmetal' },
  { atomicNumber: 16, symbol: 'S', name: 'Sulfur', group: 16, period: 3, shells: [2, 8, 6], category: 'nonmetal' },
  { atomicNumber: 17, symbol: 'Cl', name: 'Chlorine', group: 17, period: 3, shells: [2, 8, 7], category: 'halogen' },
  { atomicNumber: 18, symbol: 'Ar', name: 'Argon', group: 18, period: 3, shells: [2, 8, 8], category: 'noble-gas' },
  // Period 4
  { atomicNumber: 19, symbol: 'K', name: 'Potassium', group: 1, period: 4, shells: [2, 8, 8, 1], category: 'alkali-metal' },
  { atomicNumber: 20, symbol: 'Ca', name: 'Calcium', group: 2, period: 4, shells: [2, 8, 8, 2], category: 'alkaline-earth' },
  { atomicNumber: 21, symbol: 'Sc', name: 'Scandium', group: 3, period: 4, shells: [2, 8, 9, 2], category: 'transition-metal' },
  { atomicNumber: 22, symbol: 'Ti', name: 'Titanium', group: 4, period: 4, shells: [2, 8, 10, 2], category: 'transition-metal' },
  { atomicNumber: 23, symbol: 'V', name: 'Vanadium', group: 5, period: 4, shells: [2, 8, 11, 2], category: 'transition-metal' },
  { atomicNumber: 24, symbol: 'Cr', name: 'Chromium', group: 6, period: 4, shells: [2, 8, 13, 1], category: 'transition-metal' },
  { atomicNumber: 25, symbol: 'Mn', name: 'Manganese', group: 7, period: 4, shells: [2, 8, 13, 2], category: 'transition-metal' },
  { atomicNumber: 26, symbol: 'Fe', name: 'Iron', group: 8, period: 4, shells: [2, 8, 14, 2], category: 'transition-metal' },
  { atomicNumber: 27, symbol: 'Co', name: 'Cobalt', group: 9, period: 4, shells: [2, 8, 15, 2], category: 'transition-metal' },
  { atomicNumber: 28, symbol: 'Ni', name: 'Nickel', group: 10, period: 4, shells: [2, 8, 16, 2], category: 'transition-metal' },
  { atomicNumber: 29, symbol: 'Cu', name: 'Copper', group: 11, period: 4, shells: [2, 8, 18, 1], category: 'transition-metal' },
  { atomicNumber: 30, symbol: 'Zn', name: 'Zinc', group: 12, period: 4, shells: [2, 8, 18, 2], category: 'transition-metal' },
  { atomicNumber: 31, symbol: 'Ga', name: 'Gallium', group: 13, period: 4, shells: [2, 8, 18, 3], category: 'post-transition-metal' },
  { atomicNumber: 32, symbol: 'Ge', name: 'Germanium', group: 14, period: 4, shells: [2, 8, 18, 4], category: 'metalloid' },
  { atomicNumber: 33, symbol: 'As', name: 'Arsenic', group: 15, period: 4, shells: [2, 8, 18, 5], category: 'metalloid' },
  { atomicNumber: 34, symbol: 'Se', name: 'Selenium', group: 16, period: 4, shells: [2, 8, 18, 6], category: 'nonmetal' },
  { atomicNumber: 35, symbol: 'Br', name: 'Bromine', group: 17, period: 4, shells: [2, 8, 18, 7], category: 'halogen' },
  { atomicNumber: 36, symbol: 'Kr', name: 'Krypton', group: 18, period: 4, shells: [2, 8, 18, 8], category: 'noble-gas' },
  // Period 5
  { atomicNumber: 37, symbol: 'Rb', name: 'Rubidium', group: 1, period: 5, shells: [2, 8, 18, 8, 1], category: 'alkali-metal' },
  { atomicNumber: 38, symbol: 'Sr', name: 'Strontium', group: 2, period: 5, shells: [2, 8, 18, 8, 2], category: 'alkaline-earth' },
  { atomicNumber: 39, symbol: 'Y', name: 'Yttrium', group: 3, period: 5, shells: [2, 8, 18, 9, 2], category: 'transition-metal' },
  { atomicNumber: 40, symbol: 'Zr', name: 'Zirconium', group: 4, period: 5, shells: [2, 8, 18, 10, 2], category: 'transition-metal' },
  { atomicNumber: 41, symbol: 'Nb', name: 'Niobium', group: 5, period: 5, shells: [2, 8, 18, 12, 1], category: 'transition-metal' },
  { atomicNumber: 42, symbol: 'Mo', name: 'Molybdenum', group: 6, period: 5, shells: [2, 8, 18, 13, 1], category: 'transition-metal' },
  { atomicNumber: 43, symbol: 'Tc', name: 'Technetium', group: 7, period: 5, shells: [2, 8, 18, 13, 2], category: 'transition-metal' },
  { atomicNumber: 44, symbol: 'Ru', name: 'Ruthenium', group: 8, period: 5, shells: [2, 8, 18, 15, 1], category: 'transition-metal' },
  { atomicNumber: 45, symbol: 'Rh', name: 'Rhodium', group: 9, period: 5, shells: [2, 8, 18, 16, 1], category: 'transition-metal' },
  { atomicNumber: 46, symbol: 'Pd', name: 'Palladium', group: 10, period: 5, shells: [2, 8, 18, 18], category: 'transition-metal' },
  { atomicNumber: 47, symbol: 'Ag', name: 'Silver', group: 11, period: 5, shells: [2, 8, 18, 18, 1], category: 'transition-metal' },
  { atomicNumber: 48, symbol: 'Cd', name: 'Cadmium', group: 12, period: 5, shells: [2, 8, 18, 18, 2], category: 'transition-metal' },
  { atomicNumber: 49, symbol: 'In', name: 'Indium', group: 13, period: 5, shells: [2, 8, 18, 18, 3], category: 'post-transition-metal' },
  { atomicNumber: 50, symbol: 'Sn', name: 'Tin', group: 14, period: 5, shells: [2, 8, 18, 18, 4], category: 'post-transition-metal' },
  { atomicNumber: 51, symbol: 'Sb', name: 'Antimony', group: 15, period: 5, shells: [2, 8, 18, 18, 5], category: 'metalloid' },
  { atomicNumber: 52, symbol: 'Te', name: 'Tellurium', group: 16, period: 5, shells: [2, 8, 18, 18, 6], category: 'metalloid' },
  { atomicNumber: 53, symbol: 'I', name: 'Iodine', group: 17, period: 5, shells: [2, 8, 18, 18, 7], category: 'halogen' },
  { atomicNumber: 54, symbol: 'Xe', name: 'Xenon', group: 18, period: 5, shells: [2, 8, 18, 18, 8], category: 'noble-gas' },
  // Period 6
  { atomicNumber: 55, symbol: 'Cs', name: 'Cesium', group: 1, period: 6, shells: [2, 8, 18, 18, 8, 1], category: 'alkali-metal' },
  { atomicNumber: 56, symbol: 'Ba', name: 'Barium', group: 2, period: 6, shells: [2, 8, 18, 18, 8, 2], category: 'alkaline-earth' },
  // Lanthanides — period 8 in the UI grid (floating row below period 7).
  // group cols 4..17 line them up under La's "see below" cell.
  { atomicNumber: 57, symbol: 'La', name: 'Lanthanum', group: 3, period: 6, shells: [2, 8, 18, 18, 9, 2], category: 'lanthanide' },
  { atomicNumber: 58, symbol: 'Ce', name: 'Cerium', group: 4, period: 8, shells: [2, 8, 18, 19, 9, 2], category: 'lanthanide' },
  { atomicNumber: 59, symbol: 'Pr', name: 'Praseodymium', group: 5, period: 8, shells: [2, 8, 18, 21, 8, 2], category: 'lanthanide' },
  { atomicNumber: 60, symbol: 'Nd', name: 'Neodymium', group: 6, period: 8, shells: [2, 8, 18, 22, 8, 2], category: 'lanthanide' },
  { atomicNumber: 61, symbol: 'Pm', name: 'Promethium', group: 7, period: 8, shells: [2, 8, 18, 23, 8, 2], category: 'lanthanide' },
  { atomicNumber: 62, symbol: 'Sm', name: 'Samarium', group: 8, period: 8, shells: [2, 8, 18, 24, 8, 2], category: 'lanthanide' },
  { atomicNumber: 63, symbol: 'Eu', name: 'Europium', group: 9, period: 8, shells: [2, 8, 18, 25, 8, 2], category: 'lanthanide' },
  { atomicNumber: 64, symbol: 'Gd', name: 'Gadolinium', group: 10, period: 8, shells: [2, 8, 18, 25, 9, 2], category: 'lanthanide' },
  { atomicNumber: 65, symbol: 'Tb', name: 'Terbium', group: 11, period: 8, shells: [2, 8, 18, 27, 8, 2], category: 'lanthanide' },
  { atomicNumber: 66, symbol: 'Dy', name: 'Dysprosium', group: 12, period: 8, shells: [2, 8, 18, 28, 8, 2], category: 'lanthanide' },
  { atomicNumber: 67, symbol: 'Ho', name: 'Holmium', group: 13, period: 8, shells: [2, 8, 18, 29, 8, 2], category: 'lanthanide' },
  { atomicNumber: 68, symbol: 'Er', name: 'Erbium', group: 14, period: 8, shells: [2, 8, 18, 30, 8, 2], category: 'lanthanide' },
  { atomicNumber: 69, symbol: 'Tm', name: 'Thulium', group: 15, period: 8, shells: [2, 8, 18, 31, 8, 2], category: 'lanthanide' },
  { atomicNumber: 70, symbol: 'Yb', name: 'Ytterbium', group: 16, period: 8, shells: [2, 8, 18, 32, 8, 2], category: 'lanthanide' },
  { atomicNumber: 71, symbol: 'Lu', name: 'Lutetium', group: 17, period: 8, shells: [2, 8, 18, 32, 9, 2], category: 'lanthanide' },
  // Back to the main grid, period 6, group 4 onward (Hf takes group 4
  // since La-Lu's slot is the lanthanide row).
  { atomicNumber: 72, symbol: 'Hf', name: 'Hafnium', group: 4, period: 6, shells: [2, 8, 18, 32, 10, 2], category: 'transition-metal' },
  { atomicNumber: 73, symbol: 'Ta', name: 'Tantalum', group: 5, period: 6, shells: [2, 8, 18, 32, 11, 2], category: 'transition-metal' },
  { atomicNumber: 74, symbol: 'W', name: 'Tungsten', group: 6, period: 6, shells: [2, 8, 18, 32, 12, 2], category: 'transition-metal' },
  { atomicNumber: 75, symbol: 'Re', name: 'Rhenium', group: 7, period: 6, shells: [2, 8, 18, 32, 13, 2], category: 'transition-metal' },
  { atomicNumber: 76, symbol: 'Os', name: 'Osmium', group: 8, period: 6, shells: [2, 8, 18, 32, 14, 2], category: 'transition-metal' },
  { atomicNumber: 77, symbol: 'Ir', name: 'Iridium', group: 9, period: 6, shells: [2, 8, 18, 32, 15, 2], category: 'transition-metal' },
  { atomicNumber: 78, symbol: 'Pt', name: 'Platinum', group: 10, period: 6, shells: [2, 8, 18, 32, 17, 1], category: 'transition-metal' },
  { atomicNumber: 79, symbol: 'Au', name: 'Gold', group: 11, period: 6, shells: [2, 8, 18, 32, 18, 1], category: 'transition-metal' },
  { atomicNumber: 80, symbol: 'Hg', name: 'Mercury', group: 12, period: 6, shells: [2, 8, 18, 32, 18, 2], category: 'transition-metal' },
  { atomicNumber: 81, symbol: 'Tl', name: 'Thallium', group: 13, period: 6, shells: [2, 8, 18, 32, 18, 3], category: 'post-transition-metal' },
  { atomicNumber: 82, symbol: 'Pb', name: 'Lead', group: 14, period: 6, shells: [2, 8, 18, 32, 18, 4], category: 'post-transition-metal' },
  { atomicNumber: 83, symbol: 'Bi', name: 'Bismuth', group: 15, period: 6, shells: [2, 8, 18, 32, 18, 5], category: 'post-transition-metal' },
  { atomicNumber: 84, symbol: 'Po', name: 'Polonium', group: 16, period: 6, shells: [2, 8, 18, 32, 18, 6], category: 'post-transition-metal' },
  { atomicNumber: 85, symbol: 'At', name: 'Astatine', group: 17, period: 6, shells: [2, 8, 18, 32, 18, 7], category: 'halogen' },
  { atomicNumber: 86, symbol: 'Rn', name: 'Radon', group: 18, period: 6, shells: [2, 8, 18, 32, 18, 8], category: 'noble-gas' },
  // Period 7
  { atomicNumber: 87, symbol: 'Fr', name: 'Francium', group: 1, period: 7, shells: [2, 8, 18, 32, 18, 8, 1], category: 'alkali-metal' },
  { atomicNumber: 88, symbol: 'Ra', name: 'Radium', group: 2, period: 7, shells: [2, 8, 18, 32, 18, 8, 2], category: 'alkaline-earth' },
  // Actinides — period 9 floating row.
  { atomicNumber: 89, symbol: 'Ac', name: 'Actinium', group: 3, period: 7, shells: [2, 8, 18, 32, 18, 9, 2], category: 'actinide' },
  { atomicNumber: 90, symbol: 'Th', name: 'Thorium', group: 4, period: 9, shells: [2, 8, 18, 32, 18, 10, 2], category: 'actinide' },
  { atomicNumber: 91, symbol: 'Pa', name: 'Protactinium', group: 5, period: 9, shells: [2, 8, 18, 32, 20, 9, 2], category: 'actinide' },
  { atomicNumber: 92, symbol: 'U', name: 'Uranium', group: 6, period: 9, shells: [2, 8, 18, 32, 21, 9, 2], category: 'actinide' },
  { atomicNumber: 93, symbol: 'Np', name: 'Neptunium', group: 7, period: 9, shells: [2, 8, 18, 32, 22, 9, 2], category: 'actinide' },
  { atomicNumber: 94, symbol: 'Pu', name: 'Plutonium', group: 8, period: 9, shells: [2, 8, 18, 32, 24, 8, 2], category: 'actinide' },
  { atomicNumber: 95, symbol: 'Am', name: 'Americium', group: 9, period: 9, shells: [2, 8, 18, 32, 25, 8, 2], category: 'actinide' },
  { atomicNumber: 96, symbol: 'Cm', name: 'Curium', group: 10, period: 9, shells: [2, 8, 18, 32, 25, 9, 2], category: 'actinide' },
  { atomicNumber: 97, symbol: 'Bk', name: 'Berkelium', group: 11, period: 9, shells: [2, 8, 18, 32, 27, 8, 2], category: 'actinide' },
  { atomicNumber: 98, symbol: 'Cf', name: 'Californium', group: 12, period: 9, shells: [2, 8, 18, 32, 28, 8, 2], category: 'actinide' },
  { atomicNumber: 99, symbol: 'Es', name: 'Einsteinium', group: 13, period: 9, shells: [2, 8, 18, 32, 29, 8, 2], category: 'actinide' },
  { atomicNumber: 100, symbol: 'Fm', name: 'Fermium', group: 14, period: 9, shells: [2, 8, 18, 32, 30, 8, 2], category: 'actinide' },
  { atomicNumber: 101, symbol: 'Md', name: 'Mendelevium', group: 15, period: 9, shells: [2, 8, 18, 32, 31, 8, 2], category: 'actinide' },
  { atomicNumber: 102, symbol: 'No', name: 'Nobelium', group: 16, period: 9, shells: [2, 8, 18, 32, 32, 8, 2], category: 'actinide' },
  { atomicNumber: 103, symbol: 'Lr', name: 'Lawrencium', group: 17, period: 9, shells: [2, 8, 18, 32, 32, 8, 3], category: 'actinide' },
  // Period 7 transition + post-transition + halogen + noble gas.
  { atomicNumber: 104, symbol: 'Rf', name: 'Rutherfordium', group: 4, period: 7, shells: [2, 8, 18, 32, 32, 10, 2], category: 'transition-metal' },
  { atomicNumber: 105, symbol: 'Db', name: 'Dubnium', group: 5, period: 7, shells: [2, 8, 18, 32, 32, 11, 2], category: 'transition-metal' },
  { atomicNumber: 106, symbol: 'Sg', name: 'Seaborgium', group: 6, period: 7, shells: [2, 8, 18, 32, 32, 12, 2], category: 'transition-metal' },
  { atomicNumber: 107, symbol: 'Bh', name: 'Bohrium', group: 7, period: 7, shells: [2, 8, 18, 32, 32, 13, 2], category: 'transition-metal' },
  { atomicNumber: 108, symbol: 'Hs', name: 'Hassium', group: 8, period: 7, shells: [2, 8, 18, 32, 32, 14, 2], category: 'transition-metal' },
  { atomicNumber: 109, symbol: 'Mt', name: 'Meitnerium', group: 9, period: 7, shells: [2, 8, 18, 32, 32, 15, 2], category: 'transition-metal' },
  { atomicNumber: 110, symbol: 'Ds', name: 'Darmstadtium', group: 10, period: 7, shells: [2, 8, 18, 32, 32, 16, 2], category: 'transition-metal' },
  { atomicNumber: 111, symbol: 'Rg', name: 'Roentgenium', group: 11, period: 7, shells: [2, 8, 18, 32, 32, 17, 2], category: 'transition-metal' },
  { atomicNumber: 112, symbol: 'Cn', name: 'Copernicium', group: 12, period: 7, shells: [2, 8, 18, 32, 32, 18, 2], category: 'transition-metal' },
  { atomicNumber: 113, symbol: 'Nh', name: 'Nihonium', group: 13, period: 7, shells: [2, 8, 18, 32, 32, 18, 3], category: 'post-transition-metal' },
  { atomicNumber: 114, symbol: 'Fl', name: 'Flerovium', group: 14, period: 7, shells: [2, 8, 18, 32, 32, 18, 4], category: 'post-transition-metal' },
  { atomicNumber: 115, symbol: 'Mc', name: 'Moscovium', group: 15, period: 7, shells: [2, 8, 18, 32, 32, 18, 5], category: 'post-transition-metal' },
  { atomicNumber: 116, symbol: 'Lv', name: 'Livermorium', group: 16, period: 7, shells: [2, 8, 18, 32, 32, 18, 6], category: 'post-transition-metal' },
  { atomicNumber: 117, symbol: 'Ts', name: 'Tennessine', group: 17, period: 7, shells: [2, 8, 18, 32, 32, 18, 7], category: 'halogen' },
  { atomicNumber: 118, symbol: 'Og', name: 'Oganesson', group: 18, period: 7, shells: [2, 8, 18, 32, 32, 18, 8], category: 'noble-gas' },
];

/** Lookup by symbol — case-sensitive, matches IUPAC ('He' not 'HE'). */
export const ELEMENTS_BY_SYMBOL: ReadonlyMap<string, Element> = new Map(
  ELEMENTS.map((e) => [e.symbol, e]),
);

export function getElement(symbol: string): Element | null {
  return ELEMENTS_BY_SYMBOL.get(symbol) ?? null;
}

/**
 * Scales used by the AtomSource to quantize shell occupancy onto
 * pitched degrees. Values are semitone offsets from the root.
 *
 * Subset chosen for "always musical" leanings — chromatic is the raw
 * mode (every shell value becomes its own semitone), the rest sit
 * inside one octave with no leading-tone tension.
 */
export type AtomScaleName =
  | 'chromatic'
  | 'major'
  | 'minor'
  | 'major-pentatonic'
  | 'minor-pentatonic'
  | 'whole-tone'
  | 'lydian'
  | 'phrygian-dominant';

export const ATOM_SCALES: Readonly<Record<AtomScaleName, readonly number[]>> = {
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  'major-pentatonic': [0, 2, 4, 7, 9],
  'minor-pentatonic': [0, 3, 5, 7, 10],
  'whole-tone': [0, 2, 4, 6, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  // Major 3rd over phrygian — flamenco-y, fits Evil Synth's vibe.
  'phrygian-dominant': [0, 1, 4, 5, 7, 8, 10],
};

export const ATOM_SCALE_NAMES: readonly AtomScaleName[] = [
  'minor-pentatonic',
  'major-pentatonic',
  'minor',
  'major',
  'lydian',
  'phrygian-dominant',
  'whole-tone',
  'chromatic',
];

/**
 * Map a single shell-occupancy integer to a MIDI note above `root`.
 *
 * - **Raw mode**: shell value is added directly as semitones — Carbon's
 *   [2,4] becomes root+2, root+4 = a major 3rd dyad. Heavy elements
 *   produce wide, dissonant intervals (Gold's 32 = +32 semis ≈ Eb6).
 * - **Quantized**: shell index modulo the scale length picks a degree;
 *   the integer division of `shell / scale.length` adds octaves so
 *   bigger shells still climb. Carbon's [2,4] in major-pentatonic
 *   = scale[2]=4, scale[4]=9 = root+4, root+9 (major 3rd + 6th).
 *
 * Pure function — no side effects, easy to unit test.
 */
export function shellToMidi(
  shell: number,
  root: number,
  scale: AtomScaleName,
  raw: boolean,
): number {
  const safeRoot = Math.max(0, Math.min(127, Math.round(root)));
  if (raw) {
    return Math.max(0, Math.min(127, safeRoot + Math.max(0, Math.floor(shell))));
  }
  const intervals = ATOM_SCALES[scale];
  if (!intervals || intervals.length === 0) {
    return safeRoot;
  }
  const idx = Math.max(0, Math.floor(shell)) - 1; // shell counts start at 1
  const wrappedIdx = ((idx % intervals.length) + intervals.length) % intervals.length;
  const octave = Math.floor(idx / intervals.length);
  const interval = intervals[wrappedIdx] ?? 0;
  const midi = safeRoot + octave * 12 + interval;
  return Math.max(0, Math.min(127, midi));
}

/** Convert an element's whole shell list to a chord of MIDI notes. */
export function elementToChord(
  element: Element,
  root: number,
  scale: AtomScaleName,
  raw: boolean,
): number[] {
  return element.shells.map((s) => shellToMidi(s, root, scale, raw));
}
